import type { ReactElement } from "react"

import { GuiaDespacho } from "./GuiaDespacho"
import { SolicitudPosicionamiento } from "./SolicitudPosicionamiento"
import type { DatosPlantilla } from "./tipos"
import { ETIQUETAS, PAPEL } from "./tipos"
import { VehiculosConfrontados } from "./VehiculosConfrontados"

/**
 * La hoja: el formato que toque, a tamaño real, sobre su propio papel.
 *
 * ⚠ **Es el MISMO componente que se imprime**, como en rotulación y en los carnets.
 * Aquí va un paso más allá: no hay vista previa y formulario, hay una sola cosa — se
 * escribe encima de la hoja que se va a imprimir. En un formato que existe para
 * entregarse en ventanilla, cualquier diferencia entre lo que se ve y lo que sale es
 * un viaje perdido al puerto.
 *
 * ⚠ **El papel NO es el mismo en los tres, y la clase lo dice.** Los dos formatos de
 * la aduana son A4; la guía de despacho es Carta, como el documento del que sale y
 * como el resto de lo que imprime la agencia. El `@page` global del sistema es Carta
 * CON margen, así que ninguno de los tres sirve tal cual: cada medida tiene su `@page`
 * con nombre en `globales.css` —`plantilla` y `guia`—, que es lo que Chromium entiende
 * para dar a una parte del documento un papel distinto del resto. Con el papel
 * equivocado, el A4 se parte en dos páginas y la Carta sale con una en blanco detrás.
 *
 * ⚠ **El `switch` no tiene rama por defecto a propósito:** `DatosPlantilla` es una
 * unión cerrada, y TypeScript marca como error el día que se añada un formato y no se
 * pinte. Es la misma decisión que `VistaRotulo`.
 */
/**
 * Qué formato se pinta.
 *
 * ⚠ **Declara que devuelve un elemento y el `switch` no tiene `default`:** así, el día
 * que se añada un cuarto formato a `DatosPlantilla` y no se pinte aquí, esta función
 * acaba devolviendo `undefined` y TypeScript lo marca. Con ternarios encadenados o con
 * un `default` no habría error de compilación: habría una hoja en blanco.
 */
function hoja(datos: DatosPlantilla, onCambio: (datos: DatosPlantilla) => void): ReactElement {
  switch (datos.tipo) {
    case "POSICIONAMIENTO":
      return (
        <SolicitudPosicionamiento
          datos={datos}
          onCambio={(cambio) => onCambio({ ...datos, ...cambio })}
        />
      )
    case "VEHICULOS":
      return (
        <VehiculosConfrontados
          datos={datos}
          onCambio={(cambio) => onCambio({ ...datos, ...cambio })}
        />
      )
    case "GUIA_DESPACHO":
      return <GuiaDespacho datos={datos} onCambio={(cambio) => onCambio({ ...datos, ...cambio })} />
  }
}

export function VistaPlantilla({
  datos,
  onCambio,
}: {
  datos: DatosPlantilla
  onCambio: (datos: DatosPlantilla) => void
}) {
  const papel = PAPEL[datos.tipo]

  return (
    <article
      className={`pieza-impresa ${datos.tipo === "GUIA_DESPACHO" ? "hoja-guia" : "hoja-plantilla"}`}
      style={{
        position: "relative",
        width: `${papel.ancho}mm`,
        height: `${papel.alto}mm`,
        fontFamily: "var(--font-sans)",
      }}
      aria-label={`Formato: ${ETIQUETAS[datos.tipo]}`}
    >
      {hoja(datos, onCambio)}
    </article>
  )
}
